import React, { memo, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
import { PencilIcon } from '../../../components/icons';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import { showNotification } from '../../../redux/notification';
import { setPreventNavigation } from '../../../redux/navigate';
import ConfirmationModal from '../../../components/modals/ConfirmationModalNew';

const EmployeeIdsSection = ({ data, managementCompanyId, caregiverId, updateEmployeeIDsMutation }) => {
	const dispatch = useDispatch();

	const [isEditMode, setIsEditMode] = useState(false);
	const [localData, setLocalData] = useState([]);
	const [showConfirmationModal, setShowConfirmationModal] = useState(false);
	const [validationErrors, setValidationErrors] = useState([]);

	useEffect(() => {
		setLocalData([...data]);
	}, []);

	const [updateEmployeeIDs, { isLoading: loadingUpdate }] = updateEmployeeIDsMutation();

	if (data.length === 0) return '';

	const toggleEdit = () => setIsEditMode(!isEditMode);

	if (!isEditMode) {
		return (
			<div className="name-and-licenses__wrapper">
				<div className="d-flex justify-content-between">
					<h2>Employee IDs</h2>
					<IsAuthorizedForManagementCompany
						exactPermission="WORKFORCE.EMPLOYEE.EMPLOYEE_IDS.EDIT"
						managementCompanyId={managementCompanyId}
					>
						<button type="button" className="edit-button" onClick={toggleEdit}>
							<PencilIcon color="#7200A0" width={12} height={12} />
							Edit
						</button>
					</IsAuthorizedForManagementCompany>
				</div>
				<div className="home-details__wrapper">
					{data.map((item) => (
						<div key={item.type_id} className="d-flex justify-content-between">
							<h3>
								{item.name} {item.is_primary && <strong>(primary ID)</strong>}
							</h3>
							<h4>{item.value || ' - '}</h4>
						</div>
					))}
				</div>
			</div>
		);
	}

	const updateData = (index, value) => {
		const _empIds = [...localData];
		_empIds[index] = { ..._empIds[index], value };
		setLocalData(_empIds);
		dispatch(setPreventNavigation(true));
	};

	const getValidationErrorMessage = (typeId) => validationErrors.find((item) => item.typeId === typeId)?.message;

	const handleCancel = () => {
		setIsEditMode(false);
		setLocalData([...data]);
		setValidationErrors([]);
		dispatch(setPreventNavigation(false));
	};

	const hasChanges = () => {
		for (let i = 0; i < localData.length; i++) {
			if (localData[i].value?.trim() !== data[i].value && !(!localData[i].value?.trim() && data[i].value === null)) {
				return true;
			}
		}
		return false;
	};

	const handleSave = () => {
		if (hasChanges()) {
			setShowConfirmationModal(true);
		} else {
			handleCancel();
		}
	};

	const performSave = async () => {
		try {
			await updateEmployeeIDs({
				managementCompanyId,
				caregiverId,
				body: localData.map((item) => ({ ...item, value: item.value?.trim() ? item.value.trim() : null }))
			}).unwrap();
			dispatch(showNotification('success', `Employee IDs saved successfully.`));
			dispatch(setPreventNavigation(false));
			setIsEditMode(false);
			setValidationErrors([]);
		} catch (error) {
			const { errors, validationError } = error?.data || {};
			if (validationError && errors.length) {
				setValidationErrors([...errors]);
			}
			dispatch(
				showNotification('error', 'Changes could not be saved. Please recheck the requirements below and try again.')
			);
		} finally {
			setShowConfirmationModal(false);
		}
	};

	return [
		<div className="name-and-licenses__wrapper" key="employee-ids-edit-section">
			<h2>Employee IDs</h2>
			{localData.map((item, index) => (
				<div
					className={classNames('form-field', { 'validation-error': !!getValidationErrorMessage(item.type_id) })}
					key={item.type_id}
				>
					<div className="input-label">
						<span className="label-name">
							{item.name} {item.is_primary && <strong>(primary ID)</strong>}
						</span>
					</div>
					<input
						className="input form-input"
						type="text"
						maxLength={50}
						value={item.value || ''}
						placeholder={item.name}
						onChange={(e) => updateData(index, e.target.value)}
					/>
					<div className="error-message">{getValidationErrorMessage(item.type_id)}</div>
				</div>
			))}
			<div className="employee-ids-footer">
				<button type="button" onClick={handleCancel} className="btn-cancel">
					Cancel
				</button>
				<button
					type="button"
					disabled={loadingUpdate || !hasChanges()}
					className="btn save-button"
					onClick={handleSave}
				>
					Save changes
				</button>
			</div>
		</div>,

		showConfirmationModal && (
			<ConfirmationModal
				key="confirmation-modal"
				title="Change employee IDs?"
				size="l"
				onConfirm={performSave}
				onCancel={() => setShowConfirmationModal(false)}
				confirmLabel="Change employee IDs"
				disableBackdrop
			>
				<div style={{ marginTop: '1rem' }}>
					<p>
						You are about to update one or more employee IDs for this professional. These changes will be reflected
						wherever the employee IDs are used or displayed.
					</p>
				</div>
			</ConfirmationModal>
		)
	];
};

export default memo(EmployeeIdsSection);
